import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/client';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import PeriodToolbar from '../components/PeriodToolbar';
import { usePeriod } from '../context/PeriodContext';
import { SkeletonCard, SkeletonTable } from '../components/Skeleton';
import { ErrorState } from '../components/ErrorState';
import InlineRatingCell from '../components/InlineRatingCell';
import type { ApiError } from '../api/client';
import HubFinanceTile from '../components/HubFinanceTile';
import PerformanceSummaryCard from '../components/PerformanceSummaryCard';
import { saveBlob, slugify } from '../api/download';

type DirectReport = {
  upn: string;
  displayName: string;
};

type Period = {
  PeriodId: number;
  Name: string;
  StartDate: string;
  EndDate: string;
  IsLocked: boolean;
};

type MetricsSettings = {
  ratingScale: { score: number; label: string; percentage: number }[];
};

type AssignmentRow = {
  AssignmentId: number;
  EmployeeUpn: string;
  DisplayName: string;
  KpiCode: string;
  KpiTitle: string;
  KpiDescription?: string | null;
  IsRecurring?: boolean;
  PeriodId: number;
  PeriodName: string;
  PeriodStart?: string | null;
  PeriodEnd?: string | null;
  TargetValue: number | null;
  HasSelfRating: number;
  HasManagerRating: number;
  SelfScore: number | null;
  ManagerScore: number | null;
  SelfComment: string | null;
  ManagerComment: string | null;
  Weight: number;
};


const MONTHS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];

/** Calendar day (YYYY-MM-DD) from an ISO date or a period name such as 2026-09 / Sep 2026. */
function toIsoDay(value?: string | null): string {
  if (!value) return '';
  const raw = String(value);
  const iso = raw.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const ym = raw.match(/\b(20\d{2})[-/](\d{1,2})\b/);
  if (ym) return `${ym[1]}-${ym[2].padStart(2, '0')}-01`;
  const mon = raw.toLowerCase().match(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b/);
  const year = raw.match(/\b(20\d{2})\b/);
  if (mon && year) return `${year[1]}-${String(MONTHS.indexOf(mon[1]) + 1).padStart(2, '0')}-01`;
  return '';
}

function monthEnd(isoDay: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDay)) return '';
  const y = Number(isoDay.slice(0, 4));
  const m = Number(isoDay.slice(5, 7));
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${isoDay.slice(0, 7)}-${String(last).padStart(2, '0')}`;
}

/** Period window used by the dashboard. Never derived from PeriodId (ids are not chronological). */
function rowPeriodBounds(row: AssignmentRow, meta?: Period): { start: string; end: string } {
  const start = toIsoDay(row.PeriodStart) || toIsoDay(meta?.StartDate) || toIsoDay(row.PeriodName) || toIsoDay(meta?.Name);
  const end = toIsoDay(row.PeriodEnd) || toIsoDay(meta?.EndDate) || (start ? monthEnd(start) : '');
  return { start, end };
}

/** Score-only payload. Comment is omitted so COALESCE keeps the existing note ('' is not NULL). */
function scoreOnlyRatingBody(kpiCode: string, periodId: number, score: number) {
  return { kpiCode, periodId, score };
}

export const AssignmentsPage = () => {
  const api = useApi();
  const { user, isManager, isAdmin, realAdmin } = useAuth();
  const { dueDate } = usePeriod();
  const qc = useQueryClient();
  const [showAll, setShowAll] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending-self' | 'pending-manager' | 'complete'>('all');
  const [groupBy, setGroupBy] = useState<'none' | 'period'>('period');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'title' | 'weight' | 'status' | 'date'>('status');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [viewingEmployee, setViewingEmployee] = useState<string>(''); // empty = self
  const [savingCell, setSavingCell] = useState<string | null>(null); // "self-{id}" or "mgr-{id}"
  const [showAddKpi, setShowAddKpi] = useState(false);
  const [weightEdits, setWeightEdits] = useState<Record<number, string>>({});
  const [newKpiTitle, setNewKpiTitle] = useState('');
  const [newKpiCategory, setNewKpiCategory] = useState('General');
  const [newKpiDescription, setNewKpiDescription] = useState('');
  const [newKpiWeight, setNewKpiWeight] = useState('1.0');
  const [newKpiRecurring, setNewKpiRecurring] = useState(false);
  const [newKpiPeriodYear, setNewKpiPeriodYear] = useState(new Date().getFullYear());
  const [newKpiPeriodMonth, setNewKpiPeriodMonth] = useState(new Date().getMonth() + 1);
  const [newKpiAssignee, setNewKpiAssignee] = useState('');
  const [addKpiError, setAddKpiError] = useState<string | null>(null);
  const [groupInitKey, setGroupInitKey] = useState('');
  const [renamingCode, setRenamingCode] = useState<string | null>(null);
  const [renameTitle, setRenameTitle] = useState('');
  const [editingDescCode, setEditingDescCode] = useState<string | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [deletingCode, setDeletingCode] = useState<string | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [copyingSelfToMgr, setCopyingSelfToMgr] = useState(false);
  const dashboardRef = useRef<HTMLDivElement | null>(null);
  const [dashboardFrom, setDashboardFrom] = useState<number | 'all'>('all');
  const [dashboardTo, setDashboardTo] = useState<number | 'all'>('all');
  const periodInitRef = useRef(false);
  const rowOrderRef = useRef<number[]>([]);
  const rowOrderKeyRef = useRef('');

  /* ── Direct reports for managers ── */
  const { data: directsData } = useQuery({
    queryKey: ['directs', user?.upn],
    enabled: isManager && !!user?.upn,
    queryFn: () => api.get('/employees/directs') as Promise<DirectReport[]>,
  });
  const directs: DirectReport[] = directsData ?? [];

  const { data: assigneesData } = useQuery({
    queryKey: ['assignees-for-add-kpi', isAdmin, user?.upn],
    enabled: isManager || isAdmin,
    queryFn: async () => {
      if (isAdmin) {
        const all = await (api.get('/employees') as Promise<Array<{ Upn: string; DisplayName: string }>>);
        return all.map(e => ({ upn: e.Upn, displayName: e.DisplayName || e.Upn }));
      }
      return api.get('/employees/directs') as Promise<DirectReport[]>;
    },
  });
  const assignees: DirectReport[] = assigneesData ?? [];

  const { data: periodsData } = useQuery({
    queryKey: ['periods-for-add-kpi'],
    enabled: true,
    queryFn: () => api.get('/periods') as Promise<Period[]>,
  });
  const periods: Period[] = periodsData ?? [];

  const { data: metricsData } = useQuery({
    queryKey: ['metrics-settings'],
    queryFn: () => api.get('/settings/metrics') as Promise<MetricsSettings>,
    staleTime: 5 * 60 * 1000,
  });
  const ratingScale = metricsData?.ratingScale ?? [];
  const maxScore = ratingScale.length ? Math.max(...ratingScale.map(r => r.score)) : 5;
  const scoreLabelByValue = useMemo(() => {
    const map = new Map<number, string>();
    ratingScale.forEach(item => map.set(item.score, item.label));
    return map;
  }, [ratingScale]);

  // Map score -> configured percentage (e.g. 0→0%, 1→0%, 2→50%, 3→75%, 4→100%, 5→150%)
  const scoreToPct = useMemo(() => {
    const map = new Map<number, number>();
    ratingScale.forEach(item => map.set(item.score, item.percentage));
    return map;
  }, [ratingScale]);

  // Auto-select current period on first load (both From and To)
  useEffect(() => {
    if (!periodInitRef.current && periods.length > 0) {
      periodInitRef.current = true;
      const today = new Date().toISOString().slice(0, 10);
      const current = periods.find(p => p.StartDate <= today && p.EndDate >= today);
      if (current) {
        setDashboardFrom(current.PeriodId);
        setDashboardTo(current.PeriodId);
      }
    }
  }, [periods]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['assignments', showAll ? 'all' : dueDate, viewingEmployee || user?.upn],
    enabled: true,
    retry: 2,
    staleTime: 30000,
    queryFn: () => {
      const endDateParam = showAll ? '2099-12-31' : dueDate;
      const empParam = viewingEmployee ? `&employeeUpn=${encodeURIComponent(viewingEmployee)}` : '';
      return api.get(`/assignments/self?endDate=${endDateParam}${empParam}`);
    }
  });

  const rows: AssignmentRow[] = data?.items ?? [];

  /* ── Determine who we're viewing ── */
  const targetUpn = viewingEmployee || user?.upn || '';
  const isSelf = !viewingEmployee || viewingEmployee.toLowerCase() === (user?.upn || '').toLowerCase();
  const canManageKpis = Boolean(user?.upn);
  const showAssigneePicker = isManager || isAdmin;
  const targetForAdd = showAssigneePicker
    ? (newKpiAssignee || viewingEmployee || user?.upn || '')
    : (user?.upn || '');
  const canAddForCurrentView = Boolean(targetForAdd);

  const employeeOptions = useMemo(() => {
    // Managers and admins both get the picker. Admins use /employees (assignees);
    // managers use directs. Previously admins with isManager=false saw no picker.
    if (!isManager && !isAdmin) return [] as DirectReport[];
    const byUpn = new Map<string, DirectReport>();
    if (user?.upn) byUpn.set(user.upn.toLowerCase(), { upn: user.upn, displayName: user.displayName || user.upn });
    const pool = isAdmin ? assignees : directs;
    pool.forEach((d) => byUpn.set(d.upn.toLowerCase(), d));
    return Array.from(byUpn.values()).sort((a, b) => (a.displayName || a.upn).localeCompare(b.displayName || b.upn));
  }, [assignees, directs, isManager, isAdmin, user]);

  // Available periods for dashboard dropdown (latest to oldest by end/start date)
  const availablePeriods = useMemo(() => {
    const map = new Map<number, { id: number; name: string }>();
    rows.forEach(r => { if (!map.has(r.PeriodId)) map.set(r.PeriodId, { id: r.PeriodId, name: r.PeriodName }); });
    const metaById = new Map(periods.map(p => [p.PeriodId, p]));
    return Array.from(map.values()).sort((a, b) => {
      const pa = metaById.get(a.id);
      const pb = metaById.get(b.id);
      const ae = pa?.EndDate || pa?.StartDate || a.name;
      const be = pb?.EndDate || pb?.StartDate || b.name;
      if (ae !== be) return be.localeCompare(ae);
      return b.id - a.id;
    });
  }, [rows, periods]);

  // From/To is a date window, matching the API list filter (period_end >= from AND period_start <= to).
  // Period ids are not chronological (185=Sep 2026, 189=Aug 2026, 190=Oct 2026), so never compare PeriodId.
  const dashboardRange = useMemo(() => {
    const metaById = new Map(periods.map(p => [p.PeriodId, p]));
    const boundsOf = (id: number) => {
      const meta = metaById.get(id);
      const sample = rows.find(r => r.PeriodId === id);
      if (sample) return rowPeriodBounds(sample, meta);
      const start = toIsoDay(meta?.StartDate) || toIsoDay(meta?.Name);
      return { start, end: toIsoDay(meta?.EndDate) || (start ? monthEnd(start) : '') };
    };
    let from = dashboardFrom === 'all' ? null : boundsOf(dashboardFrom);
    let to = dashboardTo === 'all' ? null : boundsOf(dashboardTo);
    if (from && to && from.start && to.start && from.start > to.start) {
      const swap = from; from = to; to = swap;
    }
    return {
      fromStart: from?.start || null,
      toStart: to?.start || null,
      toEnd: to?.end || (to?.start ? monthEnd(to.start) : null),
    };
  }, [periods, rows, dashboardFrom, dashboardTo]);

  const dashboardRows = useMemo(() => {
    if (dashboardFrom === 'all' && dashboardTo === 'all') return rows;
    const metaById = new Map(periods.map(p => [p.PeriodId, p]));
    const rangeStart = dashboardRange.fromStart;
    const rangeEnd = dashboardRange.toEnd;
    return rows.filter(r => {
      const { start, end } = rowPeriodBounds(r, metaById.get(r.PeriodId));
      if (!start && !end) return true;
      const rowStart = start || end;
      const rowEnd = end || start;
      if (rangeStart && rowEnd < rangeStart) return false;
      if (rangeEnd && rowStart > rangeEnd) return false;
      return true;
    });
  }, [rows, periods, dashboardFrom, dashboardTo, dashboardRange]);

  const selectedDashboard = useMemo(() => {
    const total = dashboardRows.length;
    const selfRated = dashboardRows.filter((r) => r.HasSelfRating).length;
    const managerRated = dashboardRows.filter((r) => r.HasManagerRating).length;
    const selfScores = dashboardRows.filter((r) => r.SelfScore !== null).map((r) => Number(r.SelfScore));
    const managerScores = dashboardRows.filter((r) => r.ManagerScore !== null).map((r) => Number(r.ManagerScore));
    const avgSelf = selfScores.length > 0 ? (selfScores.reduce((a, b) => a + b, 0) / selfScores.length) : null;
    const avgManager = managerScores.length > 0 ? (managerScores.reduce((a, b) => a + b, 0) / managerScores.length) : null;
    // Score %: use configured percentage per score, only active (2+) – exclude 0 Not Rated & 1 Not Applicable
    const selfPcts = dashboardRows.filter((r) => r.SelfScore !== null && Number(r.SelfScore) >= 2).map((r) => scoreToPct.get(Number(r.SelfScore)) ?? 0);
    const mgrPcts = dashboardRows.filter((r) => r.ManagerScore !== null && Number(r.ManagerScore) >= 2).map((r) => scoreToPct.get(Number(r.ManagerScore)) ?? 0);
    const selfScorePct = selfPcts.length > 0 ? Math.round(selfPcts.reduce((a, b) => a + b, 0) / selfPcts.length) : 0;
    const mgrScorePct = mgrPcts.length > 0 ? Math.round(mgrPcts.reduce((a, b) => a + b, 0) / mgrPcts.length) : 0;
    // Active KPIs: manager rated 2+ (exclude 0 and 1)
    const activeRows = dashboardRows.filter((r) => r.ManagerScore !== null && Number(r.ManagerScore) >= 2);
    const activeCount = activeRows.length;
    const activePcts = activeRows.map((r) => scoreToPct.get(Number(r.ManagerScore!)) ?? 0);
    const activeAvg = activeCount > 0 ? activeRows.reduce((s, r) => s + Number(r.ManagerScore!), 0) / activeCount : null;
    const activeOutcomePct = activePcts.length > 0 ? Math.round(activePcts.reduce((a, b) => a + b, 0) / activePcts.length) : 0;
    // Weighted scores: Sum(Score × Weight) / Sum(Weight)
    const selfWtdRows = dashboardRows.filter((r) => r.SelfScore !== null && r.Weight > 0);
    const mgrWtdRows = dashboardRows.filter((r) => r.ManagerScore !== null && r.Weight > 0);
    const sumSelfWt = selfWtdRows.reduce((s, r) => s + r.Weight, 0);
    const sumMgrWt = mgrWtdRows.reduce((s, r) => s + r.Weight, 0);
    const wtdSelf = sumSelfWt > 0 ? selfWtdRows.reduce((s, r) => s + Number(r.SelfScore!) * r.Weight, 0) / sumSelfWt : null;
    const wtdManager = sumMgrWt > 0 ? mgrWtdRows.reduce((s, r) => s + Number(r.ManagerScore!) * r.Weight, 0) / sumMgrWt : null;
    return {
      total,
      selfRated,
      managerRated,
      avgSelf,
      avgManager,
      selfScorePct,
      mgrScorePct,
      activeCount,
      activeAvg,
      activeOutcomePct,
      wtdSelf,
      wtdManager,
      needsAttention: total > 0 && (selfRated < total || managerRated < total),
    };
  }, [dashboardRows, maxScore, scoreToPct]);

  type PeriodBreakdown = {
    periodId: number;
    periodName: string;
    total: number;
    selfRated: number;
    managerRated: number;
    avgSelf: number | null;
    avgManager: number | null;
    wtdSelf: number | null;
    wtdManager: number | null;
    selfPct: number;
    mgrPct: number;
    outcomePct: number;
    activeCount: number;
    activeAvg: number | null;
  };

  const periodBreakdown: PeriodBreakdown[] = useMemo(() => {
    const byPeriod = new Map<number, { name: string; rows: AssignmentRow[] }>();
    rows.forEach((r) => {
      if (!byPeriod.has(r.PeriodId)) byPeriod.set(r.PeriodId, { name: r.PeriodName, rows: [] });
      byPeriod.get(r.PeriodId)!.rows.push(r);
    });
    return Array.from(byPeriod.entries())
      .map(([periodId, { name, rows: pRows }]) => {
        const total = pRows.length;
        const selfRated = pRows.filter((r) => r.HasSelfRating).length;
        const managerRated = pRows.filter((r) => r.HasManagerRating).length;
        const selfScores = pRows.filter((r) => r.SelfScore !== null).map((r) => Number(r.SelfScore));
        const managerScores = pRows.filter((r) => r.ManagerScore !== null).map((r) => Number(r.ManagerScore));
        const avgSelf = selfScores.length > 0 ? selfScores.reduce((a, b) => a + b, 0) / selfScores.length : null;
        const avgManager = managerScores.length > 0 ? managerScores.reduce((a, b) => a + b, 0) / managerScores.length : null;
        const selfWtdRows = pRows.filter((r) => r.SelfScore !== null && r.Weight > 0);
        const mgrWtdRows = pRows.filter((r) => r.ManagerScore !== null && r.Weight > 0);
        const sumSelfWt = selfWtdRows.reduce((s, r) => s + r.Weight, 0);
        const sumMgrWt = mgrWtdRows.reduce((s, r) => s + r.Weight, 0);
        const wtdSelf = sumSelfWt > 0 ? selfWtdRows.reduce((s, r) => s + Number(r.SelfScore!) * r.Weight, 0) / sumSelfWt : null;
        const wtdManager = sumMgrWt > 0 ? mgrWtdRows.reduce((s, r) => s + Number(r.ManagerScore!) * r.Weight, 0) / sumMgrWt : null;
        const activeRows = pRows.filter((r) => r.ManagerScore !== null && Number(r.ManagerScore) >= 2);
        const activeCount = activeRows.length;
        const activeAvg = activeCount > 0 ? activeRows.reduce((s, r) => s + Number(r.ManagerScore!), 0) / activeCount : null;
        const activePcts = activeRows.map((r) => scoreToPct.get(Number(r.ManagerScore!)) ?? 0);
        const outcomePct = activePcts.length > 0 ? Math.round(activePcts.reduce((a, b) => a + b, 0) / activePcts.length) : 0;
        return {
          periodId,
          periodName: name,
          total,
          selfRated,
          managerRated,
          avgSelf,
          avgManager,
          wtdSelf,
          wtdManager,
          selfPct: total > 0 ? Math.round((selfRated / total) * 100) : 0,
          mgrPct: total > 0 ? Math.round((managerRated / total) * 100) : 0,
          outcomePct,
          activeCount,
          activeAvg,
        };
      })
      .sort((a, b) => b.periodName.localeCompare(a.periodName));
  }, [rows, maxScore, scoreToPct]);

  const patchAssignmentRow = (
    match: { kpiCode?: string; periodId?: number; employeeUpn?: string; assignmentId?: number },
    patch: (row: AssignmentRow) => AssignmentRow,
  ) => {
    qc.setQueriesData({ queryKey: ['assignments'] }, (old: unknown) => {
      const data = old as { items?: AssignmentRow[] } | undefined;
      if (!data?.items || !Array.isArray(data.items)) return old;
      const emp = (match.employeeUpn || '').toLowerCase();
      return {
        ...data,
        items: data.items.map((row) => {
          const idMatch = match.assignmentId != null && row.AssignmentId === match.assignmentId;
          const keyMatch =
            match.kpiCode != null &&
            match.periodId != null &&
            row.KpiCode === match.kpiCode &&
            row.PeriodId === match.periodId &&
            (row.EmployeeUpn || '').toLowerCase() === emp;
          if (!idMatch && !keyMatch) return row;
          return patch(row);
        }),
      };
    });
  };

  const patchAssignmentScore = (
    vars: { kpiCode: string; periodId: number; score: number; employeeUpn: string; assignmentId?: number },
    kind: 'self' | 'manager',
  ) => {
    patchAssignmentRow(vars, (row) => {
      if (kind === 'self') {
        return { ...row, SelfScore: vars.score, HasSelfRating: 1 };
      }
      return { ...row, ManagerScore: vars.score, HasManagerRating: 1 };
    });
  };

  /* ── Self-rating inline save (score only — existing comment is kept) ── */
  const selfMutation = useMutation({
    mutationFn: (vars: { kpiCode: string; periodId: number; score: number; employeeUpn: string; assignmentId: number }) =>
      api.post('/ratings/self', scoreOnlyRatingBody(vars.kpiCode, vars.periodId, vars.score), {
        'X-Employee-Upn': vars.employeeUpn,
      }),
    onSuccess: (_data, vars) => {
      patchAssignmentScore(vars, 'self');
    },
    onSettled: () => setSavingCell(null),
  });

  /* ── Manager review inline save ── */
  const mgrMutation = useMutation({
    mutationFn: (vars: { kpiCode: string; periodId: number; score: number; employeeUpn: string; assignmentId: number }) =>
      api.post('/ratings/review', scoreOnlyRatingBody(vars.kpiCode, vars.periodId, vars.score), {
        'X-Employee-Upn': vars.employeeUpn,
        'X-Manager-Upn': user?.upn || '',
      }),
    onSuccess: (_data, vars) => {
      patchAssignmentScore(vars, 'manager');
    },
    onSettled: () => setSavingCell(null),
  });

  const weightMutation = useMutation({
    mutationFn: (vars: { assignmentId: number; weight: number }) =>
      api.put(`/kpis/assignments/${vars.assignmentId}`, { weight: vars.weight }),
    onSuccess: (_data, vars) => {
      patchAssignmentRow({ assignmentId: vars.assignmentId }, (row) => ({ ...row, Weight: vars.weight }));
    },
  });

  const addKpiMutation = useMutation({
    mutationFn: async (vars: { employeeUpn: string; periodId: number; title: string; category: string; description: string; weight: number; is_recurring: boolean }) => {
      let createdCode = '';
      try {
        const created = await api.post('/kpis', {
          title: vars.title,
          category: vars.category,
          description: vars.description,
          is_recurring: vars.is_recurring,
        }) as { code?: string };
        createdCode = (created?.code || '').trim();
      } catch (err: any) {
        const detail = err?.body?.detail || '';
        if (!String(detail).toLowerCase().includes('duplicate') && !String(detail).toLowerCase().includes('already')) {
          throw err;
        }
      }
      if (!createdCode) {
        throw new Error('Failed to create KPI code for assignment');
      }
      return api.post('/kpis/assignments', {
        employee_upn: vars.employeeUpn,
        kpi_code: createdCode,
        period_id: vars.periodId,
        weight: vars.weight,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assignments'] });
      setAddKpiError(null);
      setNewKpiTitle('');
      setNewKpiDescription('');
      setNewKpiCategory('General');
      setNewKpiWeight('1.0');
      setNewKpiRecurring(false);
      setShowAddKpi(false);
    },
    onError: (err: ApiError) => {
      setAddKpiError(err?.body?.detail || err.message || 'Failed to add KPI');
    },
  });

  const showToast = (msg: string) => { setToastMsg(msg); setTimeout(() => setToastMsg(null), 2500); };

  // Same gate as manager rating cells: manager viewing someone else's assignments
  const canEditMgr = isManager && !isSelf;

  const renameMutation = useMutation({
    mutationFn: (vars: { kpiCode: string; title: string }) =>
      api.put(`/kpis/by-code/${encodeURIComponent(vars.kpiCode)}`, { title: vars.title }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assignments'] });
      setRenamingCode(null);
      showToast('KPI renamed — saved ✓');
    },
    onError: (err: any) => showToast(`Rename failed: ${err?.message || 'Unknown error'}`),
  });

  const descriptionMutation = useMutation({
    mutationFn: (vars: { kpiCode: string; description: string }) =>
      api.put(`/kpis/by-code/${encodeURIComponent(vars.kpiCode)}`, { description: vars.description }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assignments'] });
      setEditingDescCode(null);
      showToast('Sub-heading saved ✓');
    },
    onError: (err: any) => showToast(`Sub-heading save failed: ${err?.message || 'Unknown error'}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (code: string) =>
      api.delete(`/kpis/by-code/${encodeURIComponent(code)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assignments'] });
      setDeletingCode(null);
      showToast('KPI deleted ✓');
    },
    onError: (err: any) => { setDeletingCode(null); showToast(`Delete failed: ${err?.message || 'Unknown error'}`); },
  });

  const recurringMutation = useMutation({
    mutationFn: (vars: { kpiCode: string; is_recurring: boolean }) =>
      api.put(`/kpis/by-code/${encodeURIComponent(vars.kpiCode)}`, { is_recurring: vars.is_recurring }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ['assignments'] });
      const previous = qc.getQueriesData({ queryKey: ['assignments'] });
      qc.setQueriesData({ queryKey: ['assignments'] }, (old: unknown) => {
        const data = old as { items?: AssignmentRow[] } | undefined;
        if (!data?.items || !Array.isArray(data.items)) return old;
        return {
          ...data,
          items: data.items.map((row) =>
            row.KpiCode === vars.kpiCode ? { ...row, IsRecurring: vars.is_recurring } : row
          ),
        };
      });
      return { previous };
    },
    onError: (err: any, _vars, ctx) => {
      if (ctx?.previous) {
        for (const [key, data] of ctx.previous) qc.setQueryData(key, data);
      }
      showToast(`Recurring update failed: ${err?.body?.detail || err?.message || 'Unknown error'}`);
    },
    onSuccess: (_data, vars) => {
      showToast(vars.is_recurring ? 'Recurring on ✓' : 'Recurring off ✓');
    },
  });

  const selfCommentMutation = useMutation({
    mutationFn: (vars: { employeeUpn: string; kpiCode: string; periodId: number; score: number | null; comment: string }) =>
      api.post('/ratings/self', { kpiCode: vars.kpiCode, periodId: vars.periodId, score: vars.score, comment: vars.comment }, {
        'X-Employee-Upn': vars.employeeUpn,
      }),
    onSuccess: (_data, vars) => {
      patchAssignmentRow(vars, (row) => ({ ...row, SelfComment: vars.comment }));
    },
  });

  const managerCommentMutation = useMutation({
    mutationFn: (vars: { employeeUpn: string; kpiCode: string; periodId: number; score: number | null; comment: string }) =>
      api.post('/ratings/review', { kpiCode: vars.kpiCode, periodId: vars.periodId, score: vars.score, comment: vars.comment }, {
        'X-Employee-Upn': vars.employeeUpn,
        'X-Manager-Upn': user?.upn || '',
      }),
    onSuccess: (_data, vars) => {
      patchAssignmentRow(vars, (row) => ({ ...row, ManagerComment: vars.comment }));
    },
  });

  const getScoreLabel = (score: number | null): string => {
    if (score === null || score === undefined) return 'Not rated';
    const rounded = Number(score);
    const label = scoreLabelByValue.get(rounded);
    return label ? `${rounded} — ${label}` : `${rounded}`;
  };
  
  const filteredRows = useMemo(() => {
    let filtered = rows;
    
    // Apply status filter
    if (statusFilter === 'pending-self') filtered = filtered.filter(r => !r.HasSelfRating);
    else if (statusFilter === 'pending-manager') filtered = filtered.filter(r => r.HasSelfRating && !r.HasManagerRating);
    else if (statusFilter === 'complete') filtered = filtered.filter(r => r.HasSelfRating && r.HasManagerRating);
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.KpiTitle.toLowerCase().includes(query) ||
        r.KpiCode.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting. After an inline cell save the cache is patched in place —
    // keep the last order so a completed row does not jump (status sort would
    // otherwise send it to the bottom and the next unrated KPI fills the slot).
    const orderKey = `${sortBy}|${statusFilter}|${searchQuery}`;
    const prevOrder = rowOrderRef.current;
    const prevIndex = new Map(prevOrder.map((id, i) => [id, i]));
    const sameIds =
      prevOrder.length === filtered.length &&
      filtered.every((r) => prevIndex.has(r.AssignmentId));
    const keepOrder = sameIds && orderKey === rowOrderKeyRef.current && prevOrder.length > 0;

    if (keepOrder) {
      filtered = [...filtered].sort(
        (a, b) => (prevIndex.get(a.AssignmentId) ?? 0) - (prevIndex.get(b.AssignmentId) ?? 0),
      );
    } else {
      filtered = [...filtered].sort((a, b) => {
        if (sortBy === 'title') return a.KpiTitle.localeCompare(b.KpiTitle);
        if (sortBy === 'weight') return (b.Weight ?? 0) - (a.Weight ?? 0);
        if (sortBy === 'status') {
          // Pending items first (lower score = needs attention)
          const aScore = (a.HasManagerRating ? 2 : a.HasSelfRating ? 1 : 0);
          const bScore = (b.HasManagerRating ? 2 : b.HasSelfRating ? 1 : 0);
          return aScore - bScore;
        }
        if (sortBy === 'date') {
          // Most recent period first (descending by PeriodId)
          return (b.PeriodId ?? 0) - (a.PeriodId ?? 0);
        }
        return 0;
      });
    }

    rowOrderRef.current = filtered.map((r) => r.AssignmentId);
    rowOrderKeyRef.current = orderKey;
    return filtered;
  }, [rows, statusFilter, searchQuery, sortBy]);

  const copySelfToManager = async () => {
    if (!canEditMgr || copyingSelfToMgr) return;
    const targets = filteredRows.filter((r) => {
      if (r.SelfScore === null || r.SelfScore === undefined) return false;
      const selfNum = Number(r.SelfScore);
      if (Number.isNaN(selfNum)) return false;
      // Empty manager only — never overwrite an existing manager score
      if (r.ManagerScore !== null && r.ManagerScore !== undefined) return false;
      return true;
    });
    if (targets.length === 0) {
      showToast('Nothing to fill');
      return;
    }
    setCopyingSelfToMgr(true);
    let filled = 0;
    let failed = 0;
    const concurrency = 5;
    let next = 0;
    const runOne = async () => {
      while (true) {
        const i = next++;
        if (i >= targets.length) return;
        const row = targets[i];
        try {
          await mgrMutation.mutateAsync({
            kpiCode: row.KpiCode,
            periodId: row.PeriodId,
            score: Number(row.SelfScore),
            employeeUpn: row.EmployeeUpn,
            assignmentId: row.AssignmentId,
          });
          // mgrMutation.onSuccess already patchAssignmentScore(..., 'manager')
          filled += 1;
        } catch {
          failed += 1;
        }
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(concurrency, targets.length) }, () => runOne()),
    );
    setCopyingSelfToMgr(false);
    if (failed > 0) showToast(`Filled ${filled}, ${failed} failed`);
    else showToast(`Filled ${filled}`);
  };

  const groupedRows = useMemo(() => {
    if (groupBy === 'none') {
      return { 'All': filteredRows };
    }
    // Group by period name, ordered by period end date (not PeriodId — ids are not chronological).
    const groups: Record<string, AssignmentRow[]> = {};
    const metaById = new Map(periods.map(p => [p.PeriodId, p]));
    filteredRows.forEach(row => {
      const key = row.PeriodName;
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    });
    const sortKey = (name: string) => {
      const sample = groups[name][0];
      const { start, end } = rowPeriodBounds(sample, metaById.get(sample.PeriodId));
      return end || start || name;
    };
    const sorted: Record<string, AssignmentRow[]> = {};
    Object.keys(groups)
      .sort((a, b) => sortKey(b).localeCompare(sortKey(a)))
      .forEach(k => { sorted[k] = groups[k]; });
    return sorted;
  }, [filteredRows, groupBy, periods]);

  const summary = useMemo(() => {
    const total = rows.length;
    const selfDone = rows.filter(r => r.HasSelfRating).length;
    const mgrDone = rows.filter(r => r.HasManagerRating).length;
    const weight = rows.reduce((s, r) => s + (r.Weight ?? 0), 0);
    return {
      total,
      selfDone,
      mgrDone,
      pendingSelf: total - selfDone,
      pendingManager: total - mgrDone,
      weight: Number(weight.toFixed(3)),
    };
  }, [rows]);

  const toggleGroup = (groupName: string) => {
    const newCollapsed = new Set(collapsedGroups);
    if (newCollapsed.has(groupName)) {
      newCollapsed.delete(groupName);
    } else {
      newCollapsed.add(groupName);
    }
    setCollapsedGroups(newCollapsed);
  };

  const saveWeight = async (assignmentId: number, fallbackWeight: number) => {
    const raw = weightEdits[assignmentId] ?? String(fallbackWeight ?? 0);
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return;
    }
    await weightMutation.mutateAsync({ assignmentId, weight: parsed });
  };

  const submitAddKpi = async () => {
    setAddKpiError(null);
    const title = newKpiTitle.trim();
    const weight = Number(newKpiWeight);
    if (!title) {
      setAddKpiError('Title is required');
      return;
    }
    if (!Number.isFinite(weight) || weight < 0) {
      setAddKpiError('Weight must be a valid number');
      return;
    }
    if (!targetForAdd) {
      setAddKpiError('Select an employee first');
      return;
    }
    try {
      const period = await api.post('/periods', { year: newKpiPeriodYear, month: newKpiPeriodMonth }) as { PeriodId: number };
      if (!period?.PeriodId) { setAddKpiError('Failed to resolve period'); return; }
      await addKpiMutation.mutateAsync({
        employeeUpn: targetForAdd,
        periodId: period.PeriodId,
        title,
        category: newKpiCategory,
        description: newKpiDescription,
        weight,
        is_recurring: newKpiRecurring,
      });
    } catch (err: any) {
      setAddKpiError(err?.body?.detail || err?.message || 'Failed');
    }
  };

  useEffect(() => {
    if (!canManageKpis || !showAssigneePicker) return;
    if (!newKpiAssignee) {
      if (viewingEmployee) setNewKpiAssignee(viewingEmployee);
      else if (user?.upn) setNewKpiAssignee(user.upn);
      else if (assignees.length > 0) setNewKpiAssignee(assignees[0].upn);
    }
  }, [assignees, canManageKpis, newKpiAssignee, showAssigneePicker, user?.upn, viewingEmployee]);

  useEffect(() => {
    if (groupBy !== 'period') return;
    const keys = Object.keys(groupedRows);
    if (keys.length <= 1) return;
    const spansMonths = Boolean(
      dashboardRange.fromStart && dashboardRange.toEnd
      && dashboardRange.fromStart.slice(0, 7) !== dashboardRange.toEnd.slice(0, 7),
    );
    const nextKey = `${keys.join('|')}|${spansMonths ? 'span' : 'single'}`;
    if (nextKey === groupInitKey) return;
    if (!spansMonths) {
      // Latest month open; every other month stays listed as its own header.
      setCollapsedGroups(new Set(keys.slice(1)));
    } else {
      // A multi-month From/To must not tuck an in-range month (e.g. September) under a later one.
      const metaById = new Map(periods.map(p => [p.PeriodId, p]));
      const collapsed = new Set<string>();
      for (const key of keys) {
        const sample = groupedRows[key]?.[0];
        if (!sample) { collapsed.add(key); continue; }
        const { start, end } = rowPeriodBounds(sample, metaById.get(sample.PeriodId));
        const rowStart = start || end;
        const rowEnd = end || start;
        const inRange = !(
          (dashboardRange.fromStart && rowEnd && rowEnd < dashboardRange.fromStart)
          || (dashboardRange.toEnd && rowStart && rowStart > dashboardRange.toEnd)
        );
        if (!inRange) collapsed.add(key);
      }
      setCollapsedGroups(collapsed);
    }
    setGroupInitKey(nextKey);
  }, [groupBy, groupedRows, groupInitKey, dashboardRange, periods]);

  const headerStyle: React.CSSProperties = {
    background: '#f9fafb',
  };

  const selectedEmployeeName = useMemo(() => {
    if (!viewingEmployee) return user?.displayName || 'My KPI Assignments';
    return (
      assignees.find((a) => a.upn.toLowerCase() === viewingEmployee.toLowerCase())?.displayName
      || directs.find((d) => d.upn.toLowerCase() === viewingEmployee.toLowerCase())?.displayName
      || viewingEmployee
    );
  }, [assignees, directs, user?.displayName, viewingEmployee]);

  const [downloading, setDownloading] = useState(false);

  /** Excel export of the current view: employee, From/To period range, status and search. */
  const downloadKpis = async () => {
    const params = new URLSearchParams({ scope: 'self', format: 'xlsx' });
    if (viewingEmployee) params.set('employeeUpn', viewingEmployee);
    if (dashboardRange.fromStart) params.set('startDate', dashboardRange.fromStart);
    else params.set('startDate', '2000-01-01');
    if (dashboardRange.toEnd) params.set('endDate', dashboardRange.toEnd);
    else params.set('endDate', showAll ? '2099-12-31' : dueDate);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (searchQuery.trim()) params.set('q', searchQuery.trim());
    setDownloading(true);
    try {
      const blob = await api.getBlob(`/assignments/export?${params.toString()}`);
      const range = [dashboardRange.fromStart, dashboardRange.toStart].filter(Boolean).map(d => String(d).slice(0, 7));
      const rangePart = range.length ? `-${Array.from(new Set(range)).join('_')}` : '';
      saveBlob(blob as Blob, `kpis-${slugify(selectedEmployeeName, 'employee')}${rangePart}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err: any) {
      setToastMsg(`Download failed: ${err?.body?.detail || err?.message || 'unknown error'}`);
      setTimeout(() => setToastMsg(null), 3500);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      {/* Toast notification */}
      {toastMsg && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, background: toastMsg.includes('failed') ? '#dc2626' : '#065f46', color: '#fff', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', animation: 'fadeIn 0.3s' }}>
          {toastMsg}
        </div>
      )}
      <div style={{ paddingBottom: '0.8rem', marginBottom: '1rem', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '0.5rem', paddingTop: '0.25rem' }}>
          <h2 style={{ margin: 0 }}>KPI Assignments — {selectedEmployeeName}</h2>
          <button
            className={`refresh-btn${isFetching ? ' spinning' : ''}`}
            onClick={() => refetch()}
            title="Refresh data"
            disabled={isFetching}
          >↻</button>
          {canManageKpis && (
            <button
              onClick={() => setShowAddKpi(v => !v)}
              style={{ marginLeft: 'auto', padding: '7px 12px', fontSize: 13, borderRadius: 6, border: '1px solid #d1d5db', background: showAddKpi ? '#eff6ff' : '#fff', color: '#1f2937', cursor: 'pointer', fontWeight: 600 }}
            >
              {showAddKpi ? 'Close Add KPI' : '+ Add KPI'}
            </button>
          )}
        </div>

        {(isManager || isAdmin) && employeeOptions.length > 0 && (
          <div style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: 12 }}>
            <label style={{ fontSize: 14, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
              View employee:
            </label>
            <select
              value={viewingEmployee}
              onChange={(e) => setViewingEmployee(e.target.value)}
              style={{
                padding: '8px 12px', fontSize: 14, borderRadius: 6,
                border: '2px solid #d1d5db', minWidth: 300,
                background: viewingEmployee ? '#eff6ff' : '#fff',
              }}
            >
              <option key="_myself" value="">Myself</option>
              {employeeOptions.map(d => (
                <option key={d.upn} value={d.upn}>
                  {d.displayName || d.upn}
                </option>
              ))}
            </select>
            {viewingEmployee && (
              <button
                onClick={() => setViewingEmployee('')}
                style={{ padding: '6px 14px', fontSize: 13, borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}
              >
                Back to mine
              </button>
            )}
          </div>
        )}
      </div>

        <section className="sd" ref={dashboardRef} style={{ marginBottom: '0.75rem' }}>
          {/* ── header ── */}
          <div className="sd-head">
            <div>
              <span className="sd-kicker">Performance Dashboard</span>
              <h3 className="sd-name">{selectedEmployeeName}</h3>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>From</span>
              <select
                value={String(dashboardFrom)}
                onChange={(e) => setDashboardFrom(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                style={{ padding: '4px 8px', fontSize: 13, borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}
              >
                <option value="all">Earliest</option>
                {availablePeriods.map(p => (
                  <option key={p.id} value={String(p.id)}>{p.name}</option>
                ))}
              </select>
              <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>To</span>
              <select
                value={String(dashboardTo)}
                onChange={(e) => setDashboardTo(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                style={{ padding: '4px 8px', fontSize: 13, borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}
              >
                <option value="all">Latest</option>
                {availablePeriods.map(p => (
                  <option key={p.id} value={String(p.id)}>{p.name}</option>
                ))}
              </select>
              <span className={`sd-badge${selectedDashboard.needsAttention ? ' warn' : ''}`}>
                {selectedDashboard.needsAttention ? 'Needs attention' : 'On track'}
              </span>
              <button className="sd-export" onClick={downloadKpis} disabled={downloading} title="Download the KPIs in this view (employee, From/To months, status and search) as an Excel workbook with a Dashboard sheet">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                {downloading ? 'Preparing…' : 'Download KPIs'}
              </button>
            </div>
          </div>

          {/* ── top row: rings + summary tiles ── */}
          <div className="sd-top">
            <div className="sd-rings">
              <div className="sd-ring-wrap">
                <div className="sd-ring" style={{ background: `conic-gradient(#3b82f6 ${selectedDashboard.selfScorePct}%, #e6ecef ${selectedDashboard.selfScorePct}% 100%)` }}>
                  <div className="sd-ring-inner">{selectedDashboard.selfScorePct}<small>%</small></div>
                </div>
                <div className="sd-ring-lbl">Self Score</div>
              </div>
              <div className="sd-ring-wrap">
                <div className="sd-ring" style={{ background: `conic-gradient(#10b981 ${selectedDashboard.mgrScorePct}%, #e6ecef ${selectedDashboard.mgrScorePct}% 100%)` }}>
                  <div className="sd-ring-inner">{selectedDashboard.mgrScorePct}<small>%</small></div>
                </div>
                <div className="sd-ring-lbl">Mgr Score</div>
              </div>
              <div className="sd-ring-wrap">
                <div className="sd-ring sd-ring-outcome" style={{ background: `conic-gradient(#8b5cf6 ${selectedDashboard.activeOutcomePct}%, #e6ecef ${selectedDashboard.activeOutcomePct}% 100%)` }}>
                  <div className="sd-ring-inner">{selectedDashboard.activeOutcomePct}<small>%</small></div>
                </div>
                <div className="sd-ring-lbl">Outcome</div>
              </div>
            </div>

            <div className="sd-tiles">
              <div className="sd-tile">
                <div className="sd-tile-val">{selectedDashboard.total}</div>
                <div className="sd-tile-lbl">Total KPIs</div>
              </div>
              <div className="sd-tile">
                <div className="sd-tile-val">{selectedDashboard.selfRated}/{selectedDashboard.total}</div>
                <div className="sd-tile-lbl">Self Rated</div>
              </div>
              <div className="sd-tile">
                <div className="sd-tile-val">{selectedDashboard.managerRated}/{selectedDashboard.total}</div>
                <div className="sd-tile-lbl">Mgr Rated</div>
              </div>
              <div className="sd-tile sd-tile-accent">
                <div className="sd-tile-val">{selectedDashboard.activeCount}<small>/{selectedDashboard.total}</small></div>
                <div className="sd-tile-lbl">Active (2–{maxScore})</div>
              </div>
              <div className="sd-tile sd-tile-accent">
                <div className="sd-tile-val">{selectedDashboard.activeAvg !== null ? selectedDashboard.activeAvg.toFixed(1) : '—'}<small>/{maxScore}</small></div>
                <div className="sd-tile-lbl">Active Avg</div>
              </div>
              <div className="sd-tile">
                <div className="sd-tile-val">{selectedDashboard.wtdSelf !== null ? selectedDashboard.wtdSelf.toFixed(2) : '—'}</div>
                <div className="sd-tile-lbl">Wtd Self</div>
              </div>
              <div className="sd-tile">
                <div className="sd-tile-val">{selectedDashboard.wtdManager !== null ? selectedDashboard.wtdManager.toFixed(2) : '—'}</div>
                <div className="sd-tile-lbl">Wtd Manager</div>
              </div>
            </div>
          </div>

          {/* ── per-period score breakdown ── */}
          {periodBreakdown.length > 0 && (
            <div className="sd-periods">
              <div className="sd-section-title">Scores by Period</div>
              <div className="sd-period-table">
                <div className="sd-ptr sd-ptr-head">
                  <span className="sd-ptc sd-ptc-name">Period</span>
                  <span className="sd-ptc sd-ptc-num">KPIs</span>
                  <span className="sd-ptc sd-ptc-score">Self %</span>
                  <span className="sd-ptc sd-ptc-bar">&nbsp;</span>
                  <span className="sd-ptc sd-ptc-score">Mgr %</span>
                  <span className="sd-ptc sd-ptc-bar">&nbsp;</span>
                  <span className="sd-ptc sd-ptc-score">Active</span>
                  <span className="sd-ptc sd-ptc-score">Outcome</span>
                  <span className="sd-ptc sd-ptc-score">Wtd Mgr</span>
                </div>
                {periodBreakdown.map((p) => {
                  // Use configured percentages for bar widths – only scores 2+ (cap at 100% for visual)
                  const selfActive = rows.filter(r => r.PeriodId === p.periodId && r.SelfScore !== null && Number(r.SelfScore) >= 2);
                  const selfPctVal = selfActive.length > 0 ? Math.round(
                    selfActive.map(r => scoreToPct.get(Number(r.SelfScore)) ?? 0)
                      .reduce((a, b, _, arr) => a + b / arr.length, 0)
                  ) : 0;
                  const mgrActive = rows.filter(r => r.PeriodId === p.periodId && r.ManagerScore !== null && Number(r.ManagerScore) >= 2);
                  const mgrPctVal = mgrActive.length > 0 ? Math.round(
                    mgrActive.map(r => scoreToPct.get(Number(r.ManagerScore)) ?? 0)
                      .reduce((a, b, _, arr) => a + b / arr.length, 0)
                  ) : 0;
                  const selfBarW = Math.min(selfPctVal, 100);
                  const mgrBarW = Math.min(mgrPctVal, 100);
                  const selfColor = selfPctVal >= 75 ? '#3b82f6' : selfPctVal >= 50 ? '#f59e0b' : selfPctVal > 0 ? '#ef4444' : '#d1d5db';
                  const mgrColor = mgrPctVal >= 75 ? '#10b981' : mgrPctVal >= 50 ? '#f59e0b' : mgrPctVal > 0 ? '#ef4444' : '#d1d5db';
                  return (
                    <div className="sd-ptr" key={p.periodId}>
                      <span className="sd-ptc sd-ptc-name">{p.periodName}</span>
                      <span className="sd-ptc sd-ptc-num">{p.total}</span>
                      <span className="sd-ptc sd-ptc-score">{p.avgSelf !== null ? `${selfPctVal}%` : '—'}</span>
                      <span className="sd-ptc sd-ptc-bar">
                        <span className="sd-bar-track"><span className="sd-bar-fill" style={{ width: `${selfBarW}%`, background: selfColor }} /></span>
                      </span>
                      <span className="sd-ptc sd-ptc-score">{p.avgManager !== null ? `${mgrPctVal}%` : '—'}</span>
                      <span className="sd-ptc sd-ptc-bar">
                        <span className="sd-bar-track"><span className="sd-bar-fill" style={{ width: `${mgrBarW}%`, background: mgrColor }} /></span>
                      </span>
                      <span className="sd-ptc sd-ptc-num">{p.activeCount}/{p.total}</span>
                      <span className="sd-ptc sd-ptc-score sd-wtd">{p.outcomePct}%</span>
                      <span className="sd-ptc sd-ptc-score sd-wtd">{p.wtdManager !== null ? p.wtdManager.toFixed(2) : '—'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
        <PerformanceSummaryCard
          employeeUpn={targetUpn}
          employeeName={selectedEmployeeName}
          periodId={dashboardFrom !== 'all' && dashboardFrom === dashboardTo ? Number(dashboardFrom) : null}
        />

        {showAddKpi && canManageKpis && (
          <div style={{ marginBottom: '0.75rem', border: '1px solid #d1d5db', borderRadius: 8, padding: 12, background: '#f9fafb' }}>
            <div style={{ display: 'grid', gridTemplateColumns: showAssigneePicker ? '1fr 1.2fr 1fr 0.5fr 0.5fr 0.8fr' : '1.4fr 1fr 0.5fr 0.5fr 0.8fr', gap: 8, marginBottom: 8 }}>
              <input placeholder="KPI Title" value={newKpiTitle} onChange={(e) => setNewKpiTitle(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6 }} />
              {showAssigneePicker && (
                <select value={newKpiAssignee} onChange={(e) => setNewKpiAssignee(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}>
                  <option value="">Assignee</option>
                  {assignees.map(a => (
                    <option key={a.upn} value={a.upn}>{a.displayName || a.upn}</option>
                  ))}
                </select>
              )}
              <select value={newKpiCategory} onChange={(e) => setNewKpiCategory(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}>
                {['General', 'Finance', 'Leasing', 'Property', 'Admin', 'Marketing', 'Management', 'Training', 'Sales', 'Client Relations'].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <select value={newKpiPeriodYear} onChange={(e) => setNewKpiPeriodYear(Number(e.target.value))} style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}>
                {Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <select value={newKpiPeriodMonth} onChange={(e) => setNewKpiPeriodMonth(Number(e.target.value))} style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                  <option key={m} value={m}>{new Date(2000, m - 1).toLocaleString('default', { month: 'short' })}</option>
                ))}
              </select>
              <input type="number" step="0.01" min="0" value={newKpiWeight} onChange={(e) => setNewKpiWeight(e.target.value)} placeholder="Weight" style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6 }} />
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
              <input type="checkbox" checked={newKpiRecurring} onChange={(e) => setNewKpiRecurring(e.target.checked)} />
              Recurring <span style={{ color: '#9ca3af', fontSize: 12 }}>(roll into next month)</span>
            </label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input placeholder="Description (optional)" value={newKpiDescription} onChange={(e) => setNewKpiDescription(e.target.value)} style={{ flex: 1, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6 }} />
              <button
                onClick={submitAddKpi}
                disabled={addKpiMutation.isPending || !canAddForCurrentView}
                style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: addKpiMutation.isPending || !canAddForCurrentView ? '#d1d5db' : '#2563eb', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
              >
                {addKpiMutation.isPending ? 'Adding...' : 'Add KPI'}
              </button>
            </div>
            {!showAssigneePicker && (
              <div style={{ marginBottom: 8, fontSize: 12, color: '#4b5563' }}>
                KPI will be added to your own profile.
              </div>
            )}
            {!canAddForCurrentView && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#92400e' }}>Select a team member in “View employee” before adding KPI.</div>
            )}
            {addKpiError && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#b91c1c' }}>{addKpiError}</div>
            )}
          </div>
        )}

        <div style={{ position: 'sticky', top: 0, zIndex: 12, background: '#fff', paddingTop: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <input
            type="text"
            placeholder="🔍 Search KPIs by title or code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              fontSize: '15px',
              border: '2px solid #d1d5db',
              borderRadius: '6px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '14px' }}>
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
            />
            All periods
          </label>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            style={{ padding: '5px 10px', fontSize: '14px', borderRadius: '4px', border: '1px solid #d1d5db' }}
          >
            <option value="all">All Status</option>
            <option value="pending-self">⏳ Need My Rating</option>
            <option value="pending-manager">⏳ Awaiting Manager</option>
            <option value="complete">✓ Complete</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            style={{ padding: '5px 10px', fontSize: '14px', borderRadius: '4px', border: '1px solid #d1d5db' }}
          >
            <option value="status">Sort: Status</option>
            <option value="date">Sort: Most Recent</option>
            <option value="title">Sort: A-Z</option>
            <option value="weight">Sort: Weight</option>
          </select>

          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as any)}
            style={{ padding: '5px 10px', fontSize: '14px', borderRadius: '4px', border: '1px solid #d1d5db' }}
          >
            <option value="period">Group by Period</option>
            <option value="none">No Grouping</option>
          </select>

          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{ padding: '5px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}
            >
              Clear search
            </button>
          )}

          <button
            onClick={() => refetch()}
            disabled={isFetching}
            style={{ padding: '5px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}
          >
            {isFetching ? 'Refreshing…' : 'Refresh table'}
          </button>

          {canEditMgr && (
            <button
              type="button"
              onClick={() => { void copySelfToManager(); }}
              disabled={copyingSelfToMgr}
              title="Fill empty manager scores from self scores on currently visible rows"
              style={{
                padding: '5px 12px',
                fontSize: '14px',
                borderRadius: '4px',
                border: '1px solid #93c5fd',
                background: copyingSelfToMgr ? '#e5e7eb' : '#eff6ff',
                color: '#1d4ed8',
                fontWeight: 600,
                cursor: copyingSelfToMgr ? 'not-allowed' : 'pointer',
              }}
            >
              {copyingSelfToMgr ? 'Copying…' : 'Copy self → manager'}
            </button>
          )}

          <span style={{ marginLeft: 'auto', fontSize: '14px', color: '#6b7280' }}>
            {filteredRows.length} of {rows.length} KPIs
          </span>
        </div>
        </div>

      {isLoading ? (
        <>
          <div className="card-grid">
            <SkeletonCard count={5} />
          </div>
          <SkeletonTable rows={5} columns={6} />
        </>
      ) : error ? (
        <ErrorState
          title="Failed to load assignments"
          message={error instanceof Error ? error.message : 'Unknown error'}
          onRetry={refetch}
        />
      ) : (
        <>
          <div className="card-grid">
            <SummaryCard title="Total KPIs" value={summary.total} subtitle="for this period" />
            <SummaryCard title="Self Ratings" value={`${summary.selfDone}/${summary.total}`} subtitle={`${summary.pendingSelf} pending`} />
            <SummaryCard title="Manager Reviews" value={`${summary.mgrDone}/${summary.total}`} subtitle={`${summary.pendingManager} pending`} />
            <SummaryCard title="Weight Coverage" value={summary.weight || 0} subtitle="sum of weights" />
          </div>
          
          {filteredRows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
              <div style={{ fontSize: '48px', marginBottom: '1rem' }}>🔍</div>
              <h3>No KPIs found</h3>
              <p>{searchQuery ? 'Try adjusting your search or filters' : 'No assignments match your current filters'}</p>
            </div>
          ) : (
            <>
              <HubFinanceTile
                employeeUpn={viewingEmployee || user?.upn || ''}
                dueDate={dueDate}
                periodName={rows[0]?.PeriodName}
              />
              {Object.entries(groupedRows).map(([groupName, groupRows]) => {
              const isCollapsed = collapsedGroups.has(groupName);
              return (
                <div key={groupName} style={{ marginTop: '1.5rem' }}>
                  {groupBy !== 'none' && (
                    <h3 
                      onClick={() => toggleGroup(groupName)}
                      style={{ 
                        marginBottom: '1rem', 
                        color: '#374151',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        userSelect: 'none'
                      }}
                    >
                      <span style={{ fontSize: '0.9em' }}>{isCollapsed ? '▶' : '▼'}</span>
                      {groupName} 
                      <span style={{ fontWeight: 'normal', fontSize: '0.9em', color: '#6b7280' }}>
                        ({groupRows.length})
                      </span>
                    </h3>
                  )}
                  {!isCollapsed && (
                    <table>
                      <thead>
                        <tr>
                          <th style={{ ...headerStyle, minWidth: 200 }}>KPI</th>
                          <th style={{ ...headerStyle, width: 80, textAlign: 'center' }}>Weight</th>
                          <th style={{ ...headerStyle, width: 140, textAlign: 'center' }}>Self Rating</th>
                          <th style={{ ...headerStyle, width: 140, textAlign: 'center' }}>Manager Rating</th>
                          <th style={{ ...headerStyle, minWidth: 200 }}>Employee Comment</th>
                          <th style={{ ...headerStyle, minWidth: 200 }}>Manager Comment</th>
                          <th style={{ ...headerStyle, width: 70 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupRows.map(row => {
                          const selfCellId = `self-${row.AssignmentId}`;
                          const mgrCellId = `mgr-${row.AssignmentId}`;
                          const canEditSelf = isSelf || (!isSelf && isManager);
                          const canEditWeight = isAdmin || realAdmin || (isManager && !isSelf);
                                const canToggleRecurring = isAdmin || realAdmin || isManager;
                          return (
                            <tr key={row.AssignmentId}>
                              <td>
                                {renamingCode === row.KpiCode ? (
                                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                    <input
                                      value={renameTitle}
                                      onChange={e => setRenameTitle(e.target.value)}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter' && renameTitle.trim()) renameMutation.mutate({ kpiCode: row.KpiCode, title: renameTitle.trim() });
                                        if (e.key === 'Escape') setRenamingCode(null);
                                      }}
                                      autoFocus
                                      style={{ flex: 1, padding: '4px 8px', fontSize: 13, border: '1px solid #3b82f6', borderRadius: 4, outline: 'none' }}
                                    />
                                    <button onClick={() => renameTitle.trim() && renameMutation.mutate({ kpiCode: row.KpiCode, title: renameTitle.trim() })} disabled={renameMutation.isPending} style={{ padding: '3px 8px', fontSize: 12, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>✓</button>
                                    <button onClick={() => setRenamingCode(null)} style={{ padding: '3px 8px', fontSize: 12, background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 4, cursor: 'pointer' }}>✕</button>
                                  </div>
                                ) : deletingCode === row.KpiCode ? (
                                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                    <span style={{ fontSize: 13, color: '#dc2626' }}>Delete <b>{row.KpiTitle}</b>?</span>
                                    <button onClick={() => deleteMutation.mutate(row.KpiCode)} disabled={deleteMutation.isPending} style={{ padding: '3px 8px', fontSize: 12, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Yes</button>
                                    <button onClick={() => setDeletingCode(null)} style={{ padding: '3px 8px', fontSize: 12, background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 4, cursor: 'pointer' }}>No</button>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <div style={{fontWeight:600, flex: 1}}>
                                      {row.KpiTitle}
                                      {row.IsRecurring && (
                                        <span style={{
                                          marginLeft: 8, padding: '1px 7px', borderRadius: 99, fontSize: 10, fontWeight: 700,
                                          background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0',
                                          textTransform: 'uppercase', verticalAlign: 'middle',
                                        }}>Recurring</span>
                                      )}
                                      {canToggleRecurring && (
                                        <button
                                          type="button"
                                          onClick={() => recurringMutation.mutate({ kpiCode: row.KpiCode, is_recurring: !row.IsRecurring })}
                                          disabled={recurringMutation.isPending}
                                          title="Toggle monthly roll-forward"
                                          style={{
                                            marginLeft: 8, padding: '2px 8px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                                            background: row.IsRecurring ? '#ecfdf5' : '#f9fafb',
                                            color: row.IsRecurring ? '#047857' : '#6b7280',
                                            border: row.IsRecurring ? '1px solid #a7f3d0' : '1px solid #d1d5db',
                                            fontWeight: 600, verticalAlign: 'middle',
                                          }}
                                        >{row.IsRecurring ? 'Recurring on' : 'Make recurring'}</button>
                                      )}
                                    </div>
                                    {(isSelf || realAdmin) && (
                                        <button onClick={() => { setRenamingCode(row.KpiCode); setRenameTitle(row.KpiTitle); }} style={{ padding: '2px 6px', fontSize: 11, background: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }} title="Rename KPI">✏️</button>
                                    )}
                                    {realAdmin && (
                                        <button onClick={() => setDeletingCode(row.KpiCode)} style={{ padding: '2px 6px', fontSize: 11, background: 'transparent', color: '#ef4444', border: '1px solid #fca5a5', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }} title="Delete KPI">🗑️</button>
                                    )}
                                  </div>
                                )}
                                {editingDescCode === row.KpiCode ? (
                                  <div style={{ marginTop: 4 }}>
                                    <textarea
                                      value={editDescription}
                                      onChange={e => setEditDescription(e.target.value)}
                                      onKeyDown={e => {
                                        if (e.key === 'Escape') setEditingDescCode(null);
                                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                          descriptionMutation.mutate({ kpiCode: row.KpiCode, description: editDescription });
                                        }
                                      }}
                                      autoFocus
                                      rows={3}
                                      placeholder="Sub-heading / description"
                                      style={{ width: '100%', resize: 'vertical', minHeight: 56, fontSize: 12, color: '#374151', border: '1px solid #3b82f6', borderRadius: 4, padding: '6px 8px', outline: 'none', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                                    />
                                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                                      <button
                                        onClick={() => descriptionMutation.mutate({ kpiCode: row.KpiCode, description: editDescription })}
                                        disabled={descriptionMutation.isPending}
                                        style={{ padding: '3px 8px', fontSize: 11, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                                      >{descriptionMutation.isPending ? 'Saving…' : 'Save'}</button>
                                      <button
                                        onClick={() => setEditingDescCode(null)}
                                        disabled={descriptionMutation.isPending}
                                        style={{ padding: '3px 8px', fontSize: 11, background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                                      >Cancel</button>
                                      <span style={{ fontSize: 10, color: '#9ca3af', alignSelf: 'center' }}>Ctrl+Enter to save</span>
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 2 }}>
                                    <div style={{ fontSize: '0.8em', color: '#6b7280', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', flex: 1 }}>
                                      {row.KpiDescription || '—'}
                                    </div>
                                    {(isSelf || realAdmin) && (
                                      <button
                                        onClick={() => { setEditingDescCode(row.KpiCode); setEditDescription(row.KpiDescription || ''); }}
                                        style={{ padding: '1px 5px', fontSize: 10, background: 'transparent', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}
                                        title="Edit sub-heading / description"
                                      >✏️</button>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {canEditWeight ? (
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={weightEdits[row.AssignmentId] ?? String(row.Weight ?? 0)}
                                      onChange={(e) => setWeightEdits(prev => ({ ...prev, [row.AssignmentId]: e.target.value }))}
                                      style={{ width: 64, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12 }}
                                    />
                                    <button
                                      onClick={() => saveWeight(row.AssignmentId, row.Weight)}
                                      disabled={weightMutation.isPending}
                                      style={{ padding: '3px 7px', fontSize: 11, border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: 'pointer' }}
                                    >
                                      Save
                                    </button>
                                  </div>
                                ) : (
                                  row.Weight?.toFixed(2) ?? '—'
                                )}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div>
                                  <InlineRatingCell
                                    score={row.SelfScore}
                                    hasRating={Boolean(row.HasSelfRating)}
                                    editable={canEditSelf}
                                    saving={savingCell === selfCellId}
                                    maxScore={maxScore}
                                    ratingScale={ratingScale}
                                    onSave={async (score) => {
                                      setSavingCell(selfCellId);
                                      await selfMutation.mutateAsync({
                                        kpiCode: row.KpiCode,
                                        periodId: row.PeriodId,
                                        score,
                                        employeeUpn: row.EmployeeUpn,
                                        assignmentId: row.AssignmentId,
                                      });
                                    }}
                                  />
                                  <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280', whiteSpace: 'normal' }}>
                                    {getScoreLabel(row.SelfScore)}
                                  </div>
                                </div>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div>
                                  <InlineRatingCell
                                    score={row.ManagerScore}
                                    hasRating={Boolean(row.HasManagerRating)}
                                    editable={canEditMgr}
                                    saving={savingCell === mgrCellId}
                                    maxScore={maxScore}
                                    ratingScale={ratingScale}
                                    onSave={async (score) => {
                                      setSavingCell(mgrCellId);
                                      await mgrMutation.mutateAsync({
                                        kpiCode: row.KpiCode,
                                        periodId: row.PeriodId,
                                        score,
                                        employeeUpn: row.EmployeeUpn,
                                        assignmentId: row.AssignmentId,
                                      });
                                    }}
                                  />
                                  <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280', whiteSpace: 'normal' }}>
                                    {getScoreLabel(row.ManagerScore)}
                                  </div>
                                </div>
                              </td>
                              <td>
                                <ExpandableNote
                                  text={row.SelfComment}
                                  editable={canEditSelf}
                                  onSave={async (comment) => {
                                    await selfCommentMutation.mutateAsync({
                                      employeeUpn: row.EmployeeUpn,
                                      kpiCode: row.KpiCode,
                                      periodId: row.PeriodId,
                                      score: row.SelfScore,
                                      comment,
                                    });
                                  }}
                                  saving={selfCommentMutation.isPending}
                                />
                              </td>
                              <td>
                                <ExpandableNote
                                  text={row.ManagerComment}
                                  editable={canEditMgr}
                                  onSave={async (comment) => {
                                    await managerCommentMutation.mutateAsync({
                                      employeeUpn: row.EmployeeUpn,
                                      kpiCode: row.KpiCode,
                                      periodId: row.PeriodId,
                                      score: row.ManagerScore,
                                      comment,
                                    });
                                  }}
                                  saving={managerCommentMutation.isPending}
                                />
                              </td>
                              <td>
                                <Link to={`/rating/${encodeURIComponent(row.EmployeeUpn)}/${row.KpiCode}/${row.PeriodId}`} style={{ fontSize: 13 }}>Details</Link>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
            </>
          )}
        </>
      )}
    </div>
  );
};

const SummaryCard: React.FC<{ title: string; value: React.ReactNode; subtitle?: string }> = ({ title, value, subtitle }) => (
  <div className="summary-card">
    <div className="summary-card-title">{title}</div>
    <div className="summary-card-value">{value}</div>
    {subtitle && <div className="summary-card-subtitle">{subtitle}</div>}
  </div>
);

const ExpandableNote: React.FC<{
  text?: string | null;
  editable?: boolean;
  onSave?: (comment: string) => Promise<void>;
  saving?: boolean;
}> = ({ text, editable = false, onSave, saving = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text || '');
  const value = (text || '').trim();

  const startEdit = () => {
    setDraft(text || '');
    setEditing(true);
  };

  const submit = async () => {
    if (!onSave) {
      setEditing(false);
      return;
    }
    // Unchanged text (including the initial empty box) is not an edit.
    // Clearing the note and saving still stores a blank.
    if ((draft || '') !== (text || '')) {
      await onSave(draft);
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <div>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={4}
          style={{ width: '100%', resize: 'vertical', minHeight: 80, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 8px', fontSize: 12, color: '#374151' }}
        />
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          <button onClick={submit} disabled={saving} style={{ padding: '3px 8px', fontSize: 11, border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', color: '#374151', cursor: 'pointer' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={() => { setEditing(false); setDraft(text || ''); }} disabled={saving} style={{ padding: '3px 8px', fontSize: 11, border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', color: '#374151', cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (!value) {
    return (
      <div>
        <span style={{ color: '#9ca3af' }}>—</span>
        {editable && (
          <div>
            <button onClick={startEdit} style={{ marginTop: 4, padding: 0, border: 'none', background: 'transparent', color: '#2563eb', fontSize: 12, cursor: 'pointer' }}>
              Add
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'left' }}>
      <div
        style={{
          maxHeight: expanded ? 'none' : 56,
          overflow: 'hidden',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          lineHeight: 1.35,
          fontSize: 12,
          color: '#374151',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          padding: '6px 8px',
          background: '#fff',
        }}
      >
        {value}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => setExpanded(v => !v)}
          style={{ marginTop: 4, padding: 0, border: 'none', background: 'transparent', color: '#2563eb', fontSize: 12, cursor: 'pointer' }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
        {editable && (
          <button
            onClick={startEdit}
            style={{ marginTop: 4, padding: 0, border: 'none', background: 'transparent', color: '#2563eb', fontSize: 12, cursor: 'pointer' }}
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
};
